import { Box, Icon, Menu, MenuItem, Skeleton, Typography } from '@mui/material'
import AdxToolTip from 'components/custom/AdxToolTip'
import React, { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import SubmissionModal from '../../payroll-summary/components/SubmissionModal'
import AdxButton from 'components/custom/AdxButton'
import {
  downloadMonthlyReport,
  downloadSalaryReportPdf,
  downloadPerkesoSocsoEisTextFile,
} from 'services/api/modules/payroll/payroll-history'
import { downloadResponseFile } from 'services/helper/fileDownload'
import Bugsnag from '@bugsnag/js'
import useNetworkStatus from 'services/hooks/useNetworkStatus'
import { toast } from 'react-toastify'

const PayrunHeader = ({
  nextButton,
  backButton,
  handleNext,
  handleReturn,
  payrollPeriod,
  loadingData,
  time_id,
}) => {
  // const { month, year, effective_date } = payrollPeriod;
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isOffline } = useNetworkStatus()
  const [reportAnchorEl, setReportAnchorEl] = useState(null)
  const isReportMenuOpen = Boolean(reportAnchorEl)

  const reportFilenameDate = `${payrollPeriod?.month || ''}${payrollPeriod?.year || ''}`

  const { refetch: refetchMonthlyReport, isFetching: isMonthlyReportLoading } = useQuery(
    ['previewMonthlyReport', { time_id }],
    () => downloadMonthlyReport(time_id),
    {
      enabled: false,
      onSuccess: (res) => {
        downloadResponseFile(res, {
          fileName: `monthly_report_${reportFilenameDate}.xlsx`,
          contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          successMessage: 'Monthly payroll report downloaded.',
        })
      },
      onError: (error) => {
        Bugsnag.notify(error?.data || error)
      },
    }
  )

  const { refetch: refetchSalaryReportPdf, isFetching: isSalaryReportLoading } = useQuery(
    ['previewSalaryReportPdf', { time_id }],
    () => downloadSalaryReportPdf(time_id),
    {
      enabled: false,
      onSuccess: (res) => {
        downloadResponseFile(res, {
          fileName: `salary_report_${reportFilenameDate}.pdf`,
          contentType: 'application/pdf',
          successMessage: 'Salary report PDF downloaded.',
        })
      },
      onError: (error) => {
        Bugsnag.notify(error?.data || error)
      },
    }
  )

  const { refetch: refetchPerkesoSocsoEisTextFile, isFetching: isPerkesoSocsoEisLoading } = useQuery(
    ['previewPerkesoSocsoEisTextFile', { time_id }],
    () => downloadPerkesoSocsoEisTextFile(time_id),
    {
      enabled: false,
      onSuccess: (res) => {
        downloadResponseFile(res, {
          fileName: `perkeso_socso_eis_${reportFilenameDate}.txt`,
          contentType: 'text/plain',
          successMessage: 'PERKESO SOCSO + EIS text file downloaded.',
        })
      },
      onError: (error) => {
        Bugsnag.notify(error?.data || error)
      },
    }
  )

  const isReportLoading = isMonthlyReportLoading || isSalaryReportLoading || isPerkesoSocsoEisLoading

  const handleReportMenuOpen = (event) => {
    if (isOffline) {
      toast.error('Connection lost. Please reconnect and try again.')
      return
    }

    setReportAnchorEl(event.currentTarget)
  }

  const handleReportMenuClose = () => {
    setReportAnchorEl(null)
  }

  const handleMonthlyReportDownload = () => {
    handleReportMenuClose()
    refetchMonthlyReport()
  }

  const handleSalaryReportDownload = () => {
    handleReportMenuClose()
    refetchSalaryReportPdf()
  }

  const handlePerkesoSocsoEisDownload = () => {
    handleReportMenuClose()
    refetchPerkesoSocsoEisTextFile()
  }

  const handleExit = () => {
    navigate(`/app/payroll/dashboard`)
    queryClient.invalidateQueries(['activeRun'])
    queryClient.invalidateQueries(['nonActiveRun'])
    queryClient.invalidateQueries(['getHistory'])
    queryClient.invalidateQueries(['historyDropdown'])
  }
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 3,
        py: 2,
        bgcolor: 'white.main',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="h4" fontWeight={600}>
          Run Regular Payroll
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {loadingData ? (
            <Skeleton width={100} />
          ) : (
            <Typography variant="subtitle1" color="alternate.main">
              {`${payrollPeriod?.month} ${payrollPeriod?.year}`}
            </Typography>
          )}
          <Icon
            sx={{
              fontSize: 7,
              color: 'light-gray-text.main',
              marginRight: '10px',
              marginLeft: '10px',
            }}
          >
            circle
          </Icon>
          {loadingData ? (
            <Skeleton width={100} />
          ) : (
            <Typography
              variant="body1"
              color="light-gray-text.main"
            >{`Effective on ${payrollPeriod?.effective_date}`}</Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {backButton && (
          <AdxButton variant={'ghost'} onClick={handleReturn}>
            {backButton}
          </AdxButton>
        )}
        {nextButton === 'Submit payroll' && time_id && (
          <>
            <AdxButton
              variant="outlined"
              isLoading={isReportLoading}
              disabled={isReportLoading || loadingData || isOffline}
              onClick={handleReportMenuOpen}
              sx={{ minWidth: '148px' }}
            >
              Preview report
            </AdxButton>
            <Menu
              anchorEl={reportAnchorEl}
              open={isReportMenuOpen}
              onClose={handleReportMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: '8px',
                  boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
                  minWidth: '240px',
                },
              }}
            >
              <MenuItem onClick={handleMonthlyReportDownload}>Monthly report (.xlsx)</MenuItem>
              <MenuItem onClick={handleSalaryReportDownload}>Salary report (.pdf)</MenuItem>
              <MenuItem onClick={handlePerkesoSocsoEisDownload}>PERKESO SOCSO + EIS (.txt)</MenuItem>
            </Menu>
          </>
        )}
        {nextButton === 'Submit payroll' ? (
          time_id && <SubmissionModal date={payrollPeriod} time_id={time_id} />
        ) : (
          <AdxButton variant={'contained'} sx={{ width: '222px' }} onClick={handleNext}>
            {nextButton}
          </AdxButton>
        )}
        <AdxToolTip title="Save and exit" arrow>
          <Icon onClick={handleExit} sx={{ cursor: 'pointer' }}>
            close
          </Icon>
        </AdxToolTip>
      </Box>
    </Box>
  )
}

export default PayrunHeader
